export type ExchangeRateSource = "open-er-api" | "cbr" | "fallback";

/** 1 RUB → USD when live APIs are unavailable (≈ CBR USD rate) */
export const FALLBACK_RUB_TO_USD = 1 / 77;

const OPEN_ER_API_URL = "https://open.er-api.com/v6/latest/RUB";
const CBR_DAILY_JSON_URL = "https://www.cbr-xml-daily.ru/daily_json.js";

type OpenErApiResponse = {
  result?: string;
  rates?: { USD?: number };
};

type CbrDailyResponse = {
  Valute?: { USD?: { Value?: number } };
};

export function rubToUsd(rub: number, rubToUsdRate: number): number {
  return rub * rubToUsdRate;
}

export function formatRubPrice(rub: number): string {
  return new Intl.NumberFormat("ru-RU", {
    style: "currency",
    currency: "RUB",
    maximumFractionDigits: 0,
  }).format(rub);
}

export function formatUsdPrice(rub: number, rubToUsdRate: number): string {
  const usd = rubToUsd(rub, rubToUsdRate);
  const maximumFractionDigits = usd >= 100 ? 0 : usd >= 10 ? 0 : 2;

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits,
  }).format(usd);
}

export function parseOpenErApiRubToUsd(data: OpenErApiResponse): number | null {
  if (data.result !== "success") return null;
  const rate = data.rates?.USD;
  if (typeof rate !== "number" || rate <= 0) return null;
  return rate;
}

export function parseCbrRubToUsd(data: CbrDailyResponse): number | null {
  const rubPerUsd = data.Valute?.USD?.Value;
  if (typeof rubPerUsd !== "number" || rubPerUsd <= 0) return null;
  return 1 / rubPerUsd;
}

export async function fetchRubToUsdRate(): Promise<{
  rate: number;
  source: ExchangeRateSource;
}> {
  try {
    const response = await fetch(OPEN_ER_API_URL);
    if (response.ok) {
      const data = (await response.json()) as OpenErApiResponse;
      const rate = parseOpenErApiRubToUsd(data);
      if (rate !== null) return { rate, source: "open-er-api" };
    }
  } catch {
    // try fallback provider
  }

  try {
    const response = await fetch(CBR_DAILY_JSON_URL);
    if (response.ok) {
      const data = (await response.json()) as CbrDailyResponse;
      const rate = parseCbrRubToUsd(data);
      if (rate !== null) return { rate, source: "cbr" };
    }
  } catch {
    // use static fallback below
  }

  return { rate: FALLBACK_RUB_TO_USD, source: "fallback" };
}
